import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import {
  getWishlistStore,
  addToWishlist,
  removeFromWishlist,
  buildWishlistPayload,
  loadWishlistFromAPI,
  clearWishlistCache,
  refreshWishlist,
  resolveCategory,
  setWishlistCacheOptimistic,
  WISHLIST_EVENT,
} from "../utils/wishlistStore";
import { useAuth } from "../context/AuthContext";
import { useLoginModal } from "../context/LoginModalContext";
import { useWishlistToastContext } from "../context/WishlistToastContext";
import { devLog, devWarn } from "../utils/devLog";

export const WishlistContext = createContext(null);

// Check if user is authenticated (fallback for non-context usage)
const isAuthenticated = () => {
  try {
    const token = localStorage.getItem("auth_token");
    return !!token;
  } catch {
    return false;
  }
};

// Get current user ID
const getCurrentUserId = () => {
  try {
    const userStr = localStorage.getItem("auth_user") || localStorage.getItem("user");
    if (!userStr) return null;
    const user = JSON.parse(userStr);
    return user?.id || user?.user_id || null;
  } catch {
    return null;
  }
};

function useWishlistProviderValue() {
  const authContext = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const { openLoginModal } = useLoginModal();
  const { showToast } = useWishlistToastContext();

  // Load wishlist on mount and when auth changes
  useEffect(() => {
    let isMounted = true;

    const loadStore = async () => {
      // Use auth context if available, fallback to localStorage check
      const isAuth = authContext ? authContext.isAuthenticated : isAuthenticated();
      
      if (!isAuth) {
        if (isMounted) {
          setStore({ apartment: [], villa: [], plot: [] });
          setLoading(false);
        }
        return;
      }

      try {
        // Check for pending wishlist item before loading
        const pendingItemStr = sessionStorage.getItem("pending_wishlist_item");
        if (pendingItemStr) {
          try {
            const pendingItem = JSON.parse(pendingItemStr);
            devLog("Found pending wishlist item during load, adding…", {
              item: pendingItem.item?.title || pendingItem.item?.name,
              category: pendingItem.category,
            });

            // Add the pending item
            await addToWishlist(pendingItem.item, pendingItem.category);
            devLog("Added pending wishlist item");
            
            // Remove from sessionStorage
            sessionStorage.removeItem("pending_wishlist_item");
            
            // Clear cache to ensure fresh data
            clearWishlistCache();
          } catch (e) {
            console.error("Failed to add pending wishlist item during load:", e);
          }
        }
        
        devLog("Loading wishlist on mount…");
        const loadedStore = await loadWishlistFromAPI(true);

        // Don't dispatch event on mount - let components load naturally
        devLog("Wishlist loaded on mount", {
          apartment: loadedStore.apartment.length,
          villa: loadedStore.villa.length,
          plot: loadedStore.plot.length,
          total:
            loadedStore.apartment.length + loadedStore.villa.length + loadedStore.plot.length,
        });
        if (isMounted) {
          setStore(loadedStore);
          setLoading(false);
        }
      } catch (error) {
        console.error("Failed to load wishlist:", error);
        console.error("Error details:", {
          message: error.message,
          response: error.response,
          status: error.response?.status,
        });
        if (isMounted) {
          setStore({ apartment: [], villa: [], plot: [] });
          setLoading(false);
        }
      }
    };

    loadStore();

    // Listen for auth changes (login/logout)
    const handleStorageChange = (event) => {
      if (event.key === "auth_token" || event.key === "auth_user" || event.key === "user") {
        const isAuth = authContext ? authContext.isAuthenticated : isAuthenticated();
        if (!isAuth) {
          // User logged out
          clearWishlistCache();
          if (isMounted) {
            setStore({ apartment: [], villa: [], plot: [] });
          }
        } else {
          // User logged in or changed
          loadStore();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also check for auth changes via custom event (for same-tab login/logout)
    const handleAuthChange = async () => {
      // Prevent multiple simultaneous calls
      if (!isMounted) return;
      
      // Check if there's a pending wishlist item to add
      const pendingItemStr = sessionStorage.getItem("pending_wishlist_item");
      if (pendingItemStr) {
        try {
          const pendingItem = JSON.parse(pendingItemStr);
          const isAuth = authContext ? authContext.isAuthenticated : isAuthenticated();
          
          if (isAuth && pendingItem.item && pendingItem.category) {
            devLog("Auth changed, pending wishlist item — adding…", {
              item: pendingItem.item?.title || pendingItem.item?.name,
              category: pendingItem.category,
            });

            // Wait a bit to ensure auth token is available
            await new Promise((resolve) => setTimeout(resolve, 500));

            if (!isMounted) return; // Check again after delay

            try {
              // Add the pending item to the wishlist
              await addToWishlist(pendingItem.item, pendingItem.category);
              devLog("Added pending wishlist item after auth");

              // Remove from sessionStorage immediately to prevent duplicate processing
              sessionStorage.removeItem("pending_wishlist_item");

              // Clear cache and force refresh the store
              clearWishlistCache();
              const refreshedStore = await loadWishlistFromAPI(true);

              if (isMounted) {
                setStore(refreshedStore);
                setLoading(false);
                devLog("Wishlist refreshed after pending item", {
                  apartment: refreshedStore.apartment.length,
                  villa: refreshedStore.villa.length,
                  plot: refreshedStore.plot.length,
                });
                
                // Dispatch event once to notify other components (after state update)
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent(WISHLIST_EVENT));
                }, 200);
              }
              
              // Don't call loadStore here since we just updated it
              return;
            } catch (error) {
              console.error("❌ Failed to add pending wishlist item:", error);
              // Still load the store normally
            }
          }
        } catch (e) {
          console.error("Failed to process pending wishlist item:", e);
        }
      }
      
      // Normal load store (only if no pending item was processed)
      loadStore();
    };
    window.addEventListener("auth-changed", handleAuthChange);

    return () => {
      isMounted = false;
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("auth-changed", handleAuthChange);
    };
  }, [authContext?.isAuthenticated, authContext?.user?.id]);

  // Listen for wishlist updates
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let isUpdating = false; // Prevent infinite loops

    const updateStore = async () => {
      if (isUpdating) {
        devLog("Wishlist store update skipped (already updating)");
        return;
      }

      isUpdating = true;
      try {
        devLog("Wishlist event — syncing from cache");
        // Use cached store instead of API call to prevent loops
        // The store should already be updated by the component that dispatched the event
        const cachedStore = await getWishlistStore(false);
        setStore(cachedStore);
        devLog("Wishlist store synced from cache", {
          apartment: cachedStore.apartment.length,
          villa: cachedStore.villa.length,
          plot: cachedStore.plot.length,
        });
      } catch (error) {
        console.error("Failed to update wishlist:", error);
      } finally {
        isUpdating = false;
      }
    };

    window.addEventListener(WISHLIST_EVENT, updateStore);

    return () => {
      window.removeEventListener(WISHLIST_EVENT, updateStore);
    };
  }, []);

  const isWishlisted = useCallback(
    (item, propertyType) => {
      if (!store) return false;
      
      // Resolve category to ensure correct matching (villa, plot, apartment)
      const category = resolveCategory(propertyType || item?.property_type || item?.propertyType || item?.type);
      
      const payload = buildWishlistPayload(item, category);
      if (!payload) return false;
      
      // Use resolved category instead of payload.propertyType
      const list = store?.[category] || [];
      
      // Match by project_id (most reliable) or by id
      // Extract project_id from item for comparison
      const itemProjectId = String(
        item.project_id || 
        item.projectId || 
        item.id || 
        item.raw?.project_id || 
        item.raw?.projectId || 
        item.raw?.id ||
        payload.id
      );
      
      const isInList = list.some((entry) => {
        // Match by project_id first (most reliable), then by id
        const entryProjectId = String(entry.project_id || entry.id || '');
        const entryId = String(entry.id || '');
        return entryProjectId === itemProjectId || 
               entryId === itemProjectId ||
               entryProjectId === payload.id ||
               entryId === payload.id;
      });

      return isInList;
    },
    [store]
  );

  const toggleWishlist = useCallback(
    async (item, propertyType) => {
      // Resolve category first
      const category = resolveCategory(propertyType || item?.property_type || item?.propertyType || item?.type);
      
      const payload = buildWishlistPayload(item, category);
      if (!payload) {
        devWarn("Cannot toggle wishlist: missing payload", item);
        // Use a more user-friendly notification instead of alert
        return;
      }

      // Check if user is authenticated before proceeding
      const isAuth = authContext ? authContext.isAuthenticated : isAuthenticated();
      if (!isAuth) {
        // Store current path for redirect after login
        const currentPath = window.location.pathname + window.location.search;
        
        // Store pending wishlist item in sessionStorage for later execution
        const pendingWishlistItem = {
          item: item,
          category: category,
          payload: payload,
          timestamp: Date.now()
        };
        
        try {
          sessionStorage.setItem("pending_wishlist_item", JSON.stringify(pendingWishlistItem));
          devLog("Stored pending wishlist item", {
            item: item?.title || item?.name,
            category,
          });
        } catch (e) {
          console.error("Failed to store pending wishlist item:", e);
        }
        
        // Also create a pending action for the modal context (as backup)
        const pendingWishlistAction = async () => {
          try {
            const stored = sessionStorage.getItem("pending_wishlist_item");
            if (stored) {
              const { item: itemToAdd, category: categoryToAdd } = JSON.parse(stored);
              devLog("Executing pending wishlist action (login modal)");
              
              await new Promise(resolve => setTimeout(resolve, 500));
              await addToWishlist(itemToAdd, categoryToAdd);
              const refreshedStore = await loadWishlistFromAPI(true);
              setStore(refreshedStore);
              window.dispatchEvent(new CustomEvent(WISHLIST_EVENT));
              sessionStorage.removeItem("pending_wishlist_item");
            }
          } catch (error) {
            console.error("Failed to execute pending wishlist action:", error);
          }
        };
        
        openLoginModal(currentPath, pendingWishlistAction);
        return;
      }
      
      devLog("Toggle wishlist", {
        category,
        projectId: item?.project_id || item?.id,
        adding: !isWishlisted(item, category),
      });

      // Single project id used for both add and remove (must match isWishlisted logic)
      const projectIdStr = String(
        item.project_id || item.projectId || item.id ||
        item.raw?.project_id || item.raw?.projectId || item.raw?.id ||
        payload.id || ''
      );
      const wasAdd = !isWishlisted(item, category);

      try {
        if (isWishlisted(item, category)) {
          // —— REMOVE ——
          devLog("Removing from wishlist", projectIdStr, category);
          // 1. Optimistic update: remove from UI and sync to all components (heart + count) immediately
          setStore((prev) => {
            const p = prev || { apartment: [], villa: [], plot: [] };
            const list = (p[category] || []).filter((entry) => {
              const eid = String(entry.project_id || entry.id || '');
              return eid !== projectIdStr && eid !== String(payload.id);
            });
            const next = { ...p, [category]: list };
            queueMicrotask(() => setWishlistCacheOptimistic(next));
            return next;
          });
          showToast("removed", item?.title || item?.name || payload?.title || "Property");

          // 2. Remove on server and get correct delete id from current store
          const currentStore = await getWishlistStore();
          const items = currentStore[category] || [];
          const wishlistItem = items.find((entry) => {
            const eid = String(entry.project_id || entry.id || '');
            const wid = String(entry.wishlist_id || '');
            return eid === projectIdStr || eid === String(payload.id) || wid === projectIdStr;
          });
          const deleteId = wishlistItem
            ? (wishlistItem.project_id || wishlistItem.wishlist_id || wishlistItem.id)
            : projectIdStr;
          await removeFromWishlist(deleteId, category);
        } else {
          // —— ADD ——
          devLog("Adding to wishlist", projectIdStr, category);
          // 1. Optimistic update: add to UI and sync to all components (heart + count) immediately
          const idStr = String(payload.id || projectIdStr);
          const optimisticEntry = { ...payload, id: idStr, project_id: idStr };
          setStore((prev) => {
            const p = prev || { apartment: [], villa: [], plot: [] };
            const list = p[category] || [];
            if (list.some((e) => String(e.project_id || e.id || '') === projectIdStr)) return p;
            const next = { ...p, [category]: [optimisticEntry, ...list] };
            queueMicrotask(() => setWishlistCacheOptimistic(next));
            return next;
          });
          showToast("added", item?.title || item?.name || "Property");

          // 2. Add on server
          await addToWishlist(item, category);
        }

        // 3. Refresh from API so store stays in sync; brief delay so backend list is up to date
        await new Promise((r) => setTimeout(r, 300));
        let refreshedStore = await loadWishlistFromAPI(true);
        // If we just added and the API list doesn't include the item yet (stale read), keep it
        if (wasAdd) {
          const list = refreshedStore[category] || [];
          if (!list.some((e) => String(e.project_id || e.id || '') === projectIdStr)) {
            const idStr = String(payload.id || projectIdStr);
            const optimisticEntry = { ...payload, id: idStr, project_id: idStr };
            refreshedStore = {
              ...refreshedStore,
              [category]: [optimisticEntry, ...list],
            };
          }
        }
        setStore(refreshedStore);
        setWishlistCacheOptimistic(refreshedStore);
      } catch (error) {
        console.error("Failed to toggle wishlist:", error);
        // Check if it's an authentication error
        if (error?.message?.includes("login") || error?.response?.status === 401) {
          const currentPath = window.location.pathname + window.location.search;
          openLoginModal(currentPath);
        } else {
          // For other errors, show a user-friendly notification
          const errorMsg = error.message || "Failed to update wishlist. Please try again.";
          // You can replace this with a toast notification library if available
          console.error(errorMsg);
        }
      }
    },
    [isWishlisted, store, openLoginModal, authContext, setStore, showToast]
  );

  return {
    store: store || { apartment: [], villa: [], plot: [] },
    loading,
    isWishlisted,
    toggleWishlist,
  };
};

export function WishlistProvider({ children }) {
  const value = useWishlistProviderValue();
  return React.createElement(WishlistContext.Provider, { value }, children);
}

function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (ctx == null) {
    throw new Error("useWishlist must be used within WishlistProvider");
  }
  return ctx;
}

export default useWishlist;

